import { WORKSHEET_CATEGORIES } from './model';

/**
 * P039 · the worksheet CATEGORY filter (founder 2026-10-07: Q-wcat-1 B, Q-wcat-2 A, Q-wcat-3 A, Q-wcat-4 A) —
 * PURE helpers shared by the Assign drawer's "What", the pathway step's Target and the backoffice library.
 * The dropdown lists "All categories (N)" and then the categories PRESENT in the list it was given, in the
 * canonical order of `WORKSHEET_CATEGORIES` (the legacy `homework` only when a template carries it), each with
 * its count. The counts follow whatever list the caller passes — so the language switch is honoured.
 * No i18n here: `t` is injected (keys: worksheets.picker.allCategories, .optionCount, worksheets.category.*).
 */
export const ALL_CATEGORIES = 'all';

export type CategoryOption = { value: string; label: string };
type Translate = (key: string, options?: Record<string, unknown>) => string;
type WithCategory = { category: string };
type WithId = { templateId: string; category: string };

export const categoryOptions = (templates: readonly WithCategory[], t: Translate): CategoryOption[] => {
  const counts = new Map<string, number>();
  for (const w of templates) counts.set(w.category, (counts.get(w.category) ?? 0) + 1);
  const present = (WORKSHEET_CATEGORIES as readonly string[]).filter((c) => counts.has(c));
  // a category the model does not know yet is still listed (after the canonical ones), never hidden
  const unknown = Array.from(counts.keys()).filter((c) => !(WORKSHEET_CATEGORIES as readonly string[]).includes(c));
  return [
    {
      value: ALL_CATEGORIES,
      label: t('worksheets.picker.optionCount', {
        label: t('worksheets.picker.allCategories'),
        count: templates.length,
      }),
    },
    ...[...present, ...unknown].map((c) => ({
      value: c,
      label: t('worksheets.picker.optionCount', {
        label: t(`worksheets.category.${c}`),
        count: counts.get(c) ?? 0,
      }),
    })),
  ];
};

/** The templates in the chosen category; 'all' (or nothing chosen) keeps every one. */
export const filterByCategory = <T extends WithCategory>(
  templates: readonly T[],
  category: string | undefined | null,
): T[] =>
  !category || category === ALL_CATEGORIES ? [...templates] : templates.filter((w) => w.category === category);

/**
 * A chosen worksheet OUTSIDE the new category clears (Q-wcat-2 A): undefined. A worksheet the list does not know
 * (another language, or the list still loading) cannot be judged, so it is KEPT — as is any chosen worksheet
 * when the category is 'all' or empty. Nothing chosen → undefined.
 */
export const keepIfInCategory = (
  selectedTemplateId: string | undefined | null,
  templates: readonly WithId[],
  category: string | undefined | null,
): string | undefined => {
  if (!selectedTemplateId) return undefined;
  const chosen = templates.find((w) => w.templateId === selectedTemplateId);
  if (!chosen) return selectedTemplateId;
  return !category || category === ALL_CATEGORIES || chosen.category === category ? selectedTemplateId : undefined;
};
